import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Plane, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/offers", label: "Offres" },
  { to: "/visa", label: "Visa & Passeport" },
  { to: "/flight-quote", label: "Devis vol" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp_number)).catch(() => {});
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const waLink = whatsapp ? `https://wa.me/${whatsapp}` : "#";

  return (
    <div className="App min-h-screen flex flex-col bg-background">
      <header className="glass-nav sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
              <Plane className="h-5 w-5 text-accent" />
            </span>
            <span className="font-head font-extrabold text-lg text-primary tracking-tight">WORLD TRAVEL</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                  location.pathname === n.to ? "text-accent" : "text-primary/80 hover:text-accent"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && <NotificationBell />}
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to={user.role === "admin" ? "/admin" : "/client"}>
                  <Button variant="secondary" className="rounded-full" data-testid="space-link">
                    {user.role === "admin" ? "Admin" : "Mon espace"}
                  </Button>
                </Link>
                <Button variant="ghost" className="rounded-full" onClick={handleLogout} data-testid="logout-btn">
                  Déconnexion
                </Button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:block">
                <Button className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="login-nav-btn">
                  Connexion
                </Button>
              </Link>
            )}
            <button
              className="md:hidden p-2 text-primary"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 flex flex-col gap-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="px-3 py-3 rounded-xl text-base font-semibold text-primary hover:bg-secondary"
              >
                {n.label}
              </Link>
            ))}
            <div className="h-px bg-gray-100 my-2" />
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/client"} className="px-3 py-3 rounded-xl text-base font-semibold text-primary hover:bg-secondary">
                  {user.role === "admin" ? "Espace Admin" : "Mon espace"}
                </Link>
                <button onClick={handleLogout} className="text-left px-3 py-3 rounded-xl text-base font-semibold text-destructive">
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/login" className="px-3 py-3 rounded-xl text-base font-semibold text-accent-foreground bg-accent text-center">
                Connexion / Inscription
              </Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-primary text-white/80 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Plane className="h-5 w-5 text-accent" />
              <span className="font-head font-extrabold text-white">WORLD TRAVEL</span>
            </div>
            <p className="text-sm">Votre agence de voyage en ligne : séjours, hôtels et accompagnement visa jusqu'au départ.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Navigation</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-accent">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Nous contacter</h4>
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm hover:text-accent">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/50">
          © {new Date().getFullYear()} WORLD TRAVEL. Tous droits réservés.
        </div>
      </footer>

      {whatsapp && (
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="whatsapp-float"
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#25D366] text-white flex items-center justify-center soft-shadow hover:scale-105 transition-transform"
          aria-label="Contacter sur WhatsApp"
        >
          <MessageCircle className="h-7 w-7" />
        </a>
      )}
    </div>
  );
}
